import React, {
  ReactNode, RefObject, useCallback, useEffect, useMemo, useState,
} from "react"
import { ListItem } from "@chakra-ui/react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { GuestSummary } from "sharedTypes"
import { sortPlusOnes } from "utilities/arrays"

type Props = {
  guests: GuestSummary[]
  /** Plus-ones are listed under their guest unless the list is filtered */
  filtered: boolean
  /** The element the list scrolls in */
  scrollRef: RefObject<HTMLElement>
  children: (guest: GuestSummary, plusOne: boolean) => ReactNode
}

const ROW_HEIGHT = 73
const OVERSCAN = 5

/**
 * Guests with their plus-ones under them; each list renders its own rows. Only the rows in view
 * are rendered, with space kept for the others.
 */
const GuestList = ({
  guests, filtered, scrollRef, children,
}: Props) => {
  const rows = useMemo(() => guests.flatMap((guest) => [
    { guest, plusOne: false, key: guest.id },
    // A search can also list a plus-one on its own, so keys include their guest's
    ...(!filtered && guest.plusOnes ? sortPlusOnes(guest.plusOnes) : [])
      .map((plusOne) => ({ guest: plusOne, plusOne: true, key: `${guest.id}/${plusOne.id}` })),
  ]), [guests, filtered])
  const getItemKey = useCallback((index: number) => rows[index].key, [rows])

  // The scroll element is a parent: its ref is only set after this list's layout effects
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)
  useEffect(() => setScrollElement(scrollRef.current), [scrollRef])

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT,
    // Rows for the window's height until the scroll element is known, so the first paint has them
    initialRect: { width: 0, height: window.innerHeight },
    overscan: OVERSCAN,
    getItemKey,
  })
  const items = virtualizer.getVirtualItems()
  const before = items[0]?.start ?? 0
  const after = virtualizer.getTotalSize() - (items[items.length - 1]?.end ?? 0)

  return (
    <>
      {before > 0 && <ListItem aria-hidden h={`${before}px`} />}
      {items.map(({ key, index }) => (
        <ListItem
          key={key}
          ref={virtualizer.measureElement}
          data-index={index}
          data-testid="guest-list-item"
        >
          {children(rows[index].guest, rows[index].plusOne)}
        </ListItem>
      ))}
      {after > 0 && <ListItem aria-hidden h={`${after}px`} />}
    </>
  )
}

export default GuestList
